import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Heart,
  ThumbsUp,
  MessageSquare,
  Eye,
  Calendar,
  Send,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Share2,
  Frown,
  Smile,
  Angry,
  X,
  Image as ImageIcon,
  ZoomIn
} from 'lucide-react';
import { toast } from 'sonner';
import LikeButton from '../interactions/LikeButton';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/** Sanitize raw article HTML so admins can paste full HTML documents safely.
 *  Mirrors the logic from BlogArticlePage so both readers behave consistently.
 */
const sanitizeArticleHtml = (raw) => {
  if (!raw) return '';
  let html = String(raw);

  // Detect double-encoded HTML (admin pasted as text into a "code" editor)
  if (!/<\w+/.test(html) && /&lt;\w+/.test(html)) {
    html = html
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
  }

  // Drop dangerous / disruptive blocks
  html = html.replace(/<!DOCTYPE[^>]*>/gi, '');
  html = html.replace(/<\?xml[^?]*\?>/gi, '');
  html = html.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '');
  html = html.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '');
  html = html.replace(/<link\b[^>]*\/?>/gi, '');
  html = html.replace(/<meta\b[^>]*\/?>/gi, '');
  html = html.replace(/<title\b[^>]*>[\s\S]*?<\/title>/gi, '');

  // If a full document, extract only the body content
  const bodyMatch = html.match(/<body\b[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch) html = bodyMatch[1];

  // Strip leftover <html>, <head>, <body> tags (keeping their inner content)
  html = html.replace(/<\/?(html|head|body)\b[^>]*>/gi, '');

  return html.trim();
};

// Detect if content already contains HTML markup
const looksLikeHtml = (raw) => {
  if (!raw) return false;
  return /<\w+[\s>\/]/.test(String(raw)) || /&lt;\w+/.test(String(raw));
};

// Get or create visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('pharmacy_visitor_id');
  if (!visitorId) {
    visitorId = 'visitor_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('pharmacy_visitor_id', visitorId);
  }
  return visitorId;
};

const REACTIONS = [
  { type: 'heart', icon: Heart, color: 'text-red-500', label: 'J\'adore' },
  { type: 'like', icon: ThumbsUp, color: 'text-blue-500', label: 'J\'aime' },
  { type: 'wow', icon: Smile, color: 'text-yellow-500', label: 'Wow' },
  { type: 'sad', icon: Frown, color: 'text-purple-500', label: 'Triste' },
  { type: 'angry', icon: Angry, color: 'text-orange-500', label: 'Grr' },
];

const BlogSection = ({ language = 'fr', onArticleClick }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${API_URL}/blog/articles/public?limit=6`);
        setArticles(response.data.articles || []);
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#1B4D3E]" />
      </div>
    );
  }

  if (articles.length === 0) {
    return null; // Don't show section if no articles
  }

  return (
    <section className="py-12 bg-gradient-to-b from-white to-[#F0F9F6]">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-[#1B4D3E] mb-2">
            {language === 'fr' ? 'Conseils & Actualités' : 'Tips & News'}
          </h2>
          <p className="text-[#78716C]">
            {language === 'fr' ? 'Découvrez nos derniers articles santé et bien-être' : 'Discover our latest health and wellness articles'}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((article, index) => (
            <motion.div
              key={article.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Card 
                className="overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                onClick={() => onArticleClick && onArticleClick(article)}
              >
                {/* Image */}
                <div className="h-48 bg-gradient-to-br from-[#1B4D3E] to-[#2D6A4F] overflow-hidden">
                  {article.image_url ? (
                    <img 
                      src={article.image_url} 
                      alt={article.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-6xl opacity-30">📰</span>
                    </div>
                  )}
                </div>

                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg text-[#0F172A] line-clamp-2 group-hover:text-[#1B4D3E] transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-[#78716C] mt-2 line-clamp-2">
                    {article.excerpt}
                  </p>

                  <div className="flex items-center justify-between mt-4 pt-3 border-t">
                    <div className="flex items-center gap-3 text-sm text-[#78716C]">
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {article.views}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageSquare className="w-4 h-4" />
                        {article.comment_count || 0}
                      </span>
                      {article.reactions && (
                        <span className="flex items-center gap-1">
                          <Heart className="w-4 h-4 text-red-400" />
                          {Object.values(article.reactions).reduce((a, b) => a + b, 0)}
                        </span>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" className="text-[#1B4D3E]">
                      {language === 'fr' ? 'Lire' : 'Read'} →
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Article Image Gallery Component
const ArticleImageGallery = ({ images, imageUrl, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  
  // Use images array or fallback to single image_url
  const allImages = images && images.length > 0 
    ? images 
    : imageUrl 
      ? [{ id: 'single', url: imageUrl, caption: null }]
      : [];
  
  if (allImages.length === 0) return null;

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % allImages.length);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  return (
    <div className="mb-6">
      {/* Main Image Display */}
      <div 
        className="relative w-full rounded-xl overflow-hidden bg-gray-100 cursor-pointer group"
        style={{ aspectRatio: '16/9' }}
        onClick={() => setLightboxOpen(true)}
      >
        <img 
          src={allImages[currentIndex]?.url} 
          alt={title}
          className="w-full h-full object-contain bg-gray-50"
        />
        
        {/* Zoom icon overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-3">
            <ZoomIn className="w-6 h-6 text-gray-700" />
          </div>
        </div>

        {/* Navigation arrows (if multiple images) */}
        {allImages.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); goToPrev(); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow-lg hover:bg-white transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-700" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); goToNext(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow-lg hover:bg-white transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-700" />
            </button>
          </>
        )}

        {/* Image counter */}
        {allImages.length > 1 && (
          <div className="absolute bottom-3 right-3 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
            {currentIndex + 1} / {allImages.length}
          </div>
        )}
      </div>

      {/* Caption */}
      {allImages[currentIndex]?.caption && (
        <p className="text-sm text-gray-500 mt-2 text-center italic">
          {allImages[currentIndex].caption}
        </p>
      )}

      {/* Thumbnails (if multiple images) */}
      {allImages.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
          {allImages.map((img, index) => (
            <button
              key={img.id || index}
              onClick={() => setCurrentIndex(index)}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                index === currentIndex 
                  ? 'border-[#1B4D3E] ring-2 ring-[#1B4D3E]/30' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <img 
                src={img.url} 
                alt=""
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
            onClick={() => setLightboxOpen(false)}
          >
            {/* Close button */}
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors z-10"
            >
              <X className="w-8 h-8" />
            </button>

            {/* Navigation */}
            {allImages.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); goToPrev(); }}
                  className="absolute left-4 p-3 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors z-10"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); goToNext(); }}
                  className="absolute right-4 p-3 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors z-10"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </>
            )}

            {/* Full size image */}
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="max-w-6xl max-h-[90vh] mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={allImages[currentIndex]?.url}
                alt={title}
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
              />
              
              {/* Caption & counter */}
              <div className="text-center mt-4 text-white">
                {allImages[currentIndex]?.caption && (
                  <p className="text-white/80">{allImages[currentIndex].caption}</p>
                )}
                {allImages.length > 1 && (
                  <p className="text-white/50 text-sm mt-2">
                    {currentIndex + 1} / {allImages.length}
                  </p>
                )}
              </div>
            </motion.div>

            {/* Thumbnail strip */}
            {allImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 max-w-xl overflow-x-auto px-4 py-2 bg-black/50 rounded-full">
                {allImages.map((img, index) => (
                  <button
                    key={img.id || index}
                    onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
                    className={`flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden transition-all ${
                      index === currentIndex 
                        ? 'ring-2 ring-white scale-110' 
                        : 'opacity-50 hover:opacity-100'
                    }`}
                  >
                    <img src={img.url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Full Article View Component
const ArticleView = ({ article, language = 'fr', onBack }) => {
  const [fullArticle, setFullArticle] = useState(article);
  const [loading, setLoading] = useState(true);
  const [commentName, setCommentName] = useState('');
  const [commentContent, setCommentContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [visitorReaction, setVisitorReaction] = useState(null);
  
  const visitorId = getVisitorId();

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await axios.get(`${API_URL}/blog/articles/${article.id}`);
        setFullArticle(response.data);
        
        // Get visitor's reaction
        const reactionsRes = await axios.get(`${API_URL}/blog/articles/${article.id}/reactions?visitor_id=${visitorId}`);
        setVisitorReaction(reactionsRes.data.visitor_reaction);
      } catch (error) {
        console.error('Error fetching article:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [article.id, visitorId]);

  const handleReaction = async (reactionType) => {
    try {
      const response = await axios.post(`${API_URL}/blog/articles/${article.id}/reactions`, {
        reaction_type: reactionType,
        visitor_id: visitorId
      });
      
      if (response.data.action === 'added') {
        setVisitorReaction(reactionType);
        toast.success(language === 'fr' ? 'Réaction ajoutée !' : 'Reaction added!');
      } else {
        setVisitorReaction(null);
        toast.info(language === 'fr' ? 'Réaction retirée' : 'Reaction removed');
      }
      
      // Refresh reactions
      const reactionsRes = await axios.get(`${API_URL}/blog/articles/${article.id}/reactions?visitor_id=${visitorId}`);
      setFullArticle(prev => ({ ...prev, reactions: reactionsRes.data }));
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
    setShowReactions(false);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentName.trim() || !commentContent.trim()) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs' : 'Please fill all fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/blog/articles/${article.id}/comments`, {
        author_name: commentName,
        content: commentContent
      });
      toast.success(language === 'fr' ? 'Commentaire soumis ! Il sera visible après modération.' : 'Comment submitted! It will be visible after moderation.');
      setCommentName('');
      setCommentContent('');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de l\'envoi' : 'Error sending');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#1B4D3E]" />
      </div>
    );
  }

  const totalReactions = fullArticle.reactions 
    ? Object.values(fullArticle.reactions).reduce((a, b) => a + b, 0) 
    : 0;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Back button */}
      <Button variant="ghost" onClick={onBack} className="mb-6 text-[#1B4D3E]">
        <ChevronLeft className="w-4 h-4 mr-1" />
        {language === 'fr' ? 'Retour' : 'Back'}
      </Button>

      {/* Article */}
      <article>
        {/* Images Gallery */}
        <ArticleImageGallery 
          images={fullArticle.images} 
          imageUrl={fullArticle.image_url}
          title={fullArticle.title}
        />

        {/* Title */}
        <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4">
          {fullArticle.title}
        </h1>

        {/* Meta */}
        <div className="flex items-center gap-4 text-sm text-[#78716C] mb-6">
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {formatDate(fullArticle.published_at || fullArticle.created_at)}
          </span>
          <span className="flex items-center gap-1">
            <Eye className="w-4 h-4" />
            {fullArticle.views} {language === 'fr' ? 'vues' : 'views'}
          </span>
        </div>

        {/* Content */}
        {looksLikeHtml(fullArticle.content) ? (
          <div
            className="prose prose-lg max-w-none text-[#374151] prose-headings:font-serif prose-a:text-[#1B4D3E] prose-strong:text-[#0F172A]"
            dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(fullArticle.content) }}
          />
        ) : (
          <div className="prose prose-lg max-w-none text-[#374151] whitespace-pre-wrap">
            {fullArticle.content}
          </div>
        )}

        {/* Reactions */}
        <div className="mt-8 pt-6 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Unified Like Button */}
              <LikeButton
                targetType="blog_article"
                targetId={article.id}
                size="default"
                showNames={true}
                language={language}
              />
              
              {/* Existing reactions */}
              <div className="relative">
                <Button 
                  variant="outline" 
                  onClick={() => setShowReactions(!showReactions)}
                  className={visitorReaction ? 'border-[#1B4D3E] text-[#1B4D3E]' : ''}
                >
                  {visitorReaction ? (
                    <>
                      {React.createElement(REACTIONS.find(r => r.type === visitorReaction)?.icon || Heart, { className: `w-5 h-5 mr-2 ${REACTIONS.find(r => r.type === visitorReaction)?.color}` })}
                      {REACTIONS.find(r => r.type === visitorReaction)?.label}
                    </>
                  ) : (
                    <>
                      <Smile className="w-5 h-5 mr-2" />
                      {language === 'fr' ? 'Réagir' : 'React'}
                    </>
                  )}
                  <Badge variant="secondary" className="ml-2">{totalReactions}</Badge>
                </Button>
                
                {/* Reactions popup */}
                {showReactions && (
                  <div className="absolute bottom-full left-0 mb-2 bg-white rounded-full shadow-lg px-2 py-1 flex gap-1">
                    {REACTIONS.map(reaction => (
                      <button
                        key={reaction.type}
                        onClick={() => handleReaction(reaction.type)}
                        className={`p-2 rounded-full hover:bg-gray-100 transition-colors ${visitorReaction === reaction.type ? 'bg-gray-100' : ''}`}
                        title={reaction.label}
                      >
                        <reaction.icon className={`w-6 h-6 ${reaction.color}`} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Button variant="ghost" onClick={async () => {
              // URL OG côté backend pour que WhatsApp/iMessage scrapent les bonnes balises.
              // On utilise window.location.origin (et non REACT_APP_BACKEND_URL) :
              // l'URL partagée correspond toujours au domaine où est l'utilisateur,
              // donc l'article existe bien dans la DB de ce domaine.
              const slug = fullArticle.slug || fullArticle.id;
              const origin = typeof window !== 'undefined' ? window.location.origin : '';
              const shareUrl = slug && origin
                ? `${origin}/api/blog/og/${slug}`
                : (typeof window !== 'undefined' ? window.location.href : '');
              const copyFallback = async () => {
                try {
                  await navigator.clipboard.writeText(shareUrl);
                  toast.success(language === 'fr' ? 'Lien copié !' : 'Link copied!');
                } catch (_) {
                  toast.info(language === 'fr' ? 'Impossible de partager dans cet environnement.' : 'Sharing not available here.');
                }
              };
              // navigator.share peut throw SYNC dans une iframe tierce (Feature-Policy)
              // → on protège tout l'appel par try/catch et on retombe sur clipboard.
              try {
                if (navigator.share) {
                  await navigator.share({ title: fullArticle.title, url: shareUrl });
                  return;
                }
                await copyFallback();
              } catch (err) {
                if (err && err.name === 'AbortError') return; // user cancelled
                await copyFallback();
              }
            }} data-testid="blog-section-share-btn">
              <Share2 className="w-5 h-5 mr-2" />
              {language === 'fr' ? 'Partager' : 'Share'}
            </Button>
          </div>

          {/* Show reaction counts */}
          {totalReactions > 0 && (
            <div className="flex gap-4 mt-3 text-sm text-[#78716C]">
              {REACTIONS.map(reaction => {
                const count = fullArticle.reactions?.[reaction.type] || 0;
                if (count === 0) return null;
                return (
                  <span key={reaction.type} className="flex items-center gap-1">
                    <reaction.icon className={`w-4 h-4 ${reaction.color}`} />
                    {count}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Comments Section */}
        <div className="mt-8 pt-6 border-t">
          <h3 className="text-xl font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            {language === 'fr' ? 'Commentaires' : 'Comments'} ({fullArticle.comments?.length || 0})
          </h3>

          {/* Comment Form */}
          <form onSubmit={handleSubmitComment} className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium mb-3">{language === 'fr' ? 'Laisser un commentaire' : 'Leave a comment'}</h4>
            <Input
              placeholder={language === 'fr' ? 'Votre nom' : 'Your name'}
              value={commentName}
              onChange={(e) => setCommentName(e.target.value)}
              className="mb-3"
            />
            <textarea
              placeholder={language === 'fr' ? 'Votre commentaire...' : 'Your comment...'}
              value={commentContent}
              onChange={(e) => setCommentContent(e.target.value)}
              className="w-full p-3 border rounded-md min-h-[100px] resize-y"
            />
            <Button type="submit" disabled={submitting} className="mt-3 bg-[#1B4D3E] hover:bg-[#2D6A4F]">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {language === 'fr' ? 'Envoyer' : 'Send'}
            </Button>
          </form>

          {/* Existing Comments */}
          <div className="space-y-4">
            {fullArticle.comments?.length > 0 ? (
              fullArticle.comments.map(comment => (
                <div key={comment.id} className="p-4 bg-white border rounded-lg">
                  <div className="flex justify-between items-start">
                    <span className="font-medium text-[#0F172A]">{comment.author_name}</span>
                    <span className="text-xs text-[#78716C]">{formatDate(comment.created_at)}</span>
                  </div>
                  <p className="mt-2 text-[#374151]">{comment.content}</p>
                </div>
              ))
            ) : (
              <p className="text-center text-[#78716C] py-6">
                {language === 'fr' ? 'Soyez le premier à commenter !' : 'Be the first to comment!'}
              </p>
            )}
          </div>
        </div>
      </article>
    </div>
  );
};

export { BlogSection, ArticleView };
export default BlogSection;
